/**
 * useIsDesktop — a JS mirror of the Tailwind `md` (≥768px) breakpoint.
 *
 * The layout itself is CSS-driven (Tailwind `md:` utilities), but two
 * primitives branch STRUCTURALLY on a boolean rather than on CSS: the
 * `BottomSheet` (`desktop` → slides in from the right vs. up from the
 * bottom) and the map height. This hook supplies that boolean from a single
 * `matchMedia` listener, SSR-safe (returns `false` when `window` is absent)
 * and re-rendering only on the actual breakpoint crossing.
 */
import { useEffect, useState } from "react";

/** The `md` breakpoint (Tailwind default). */
export const DESKTOP_QUERY = "(min-width: 768px)";

function readMatch(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia(DESKTOP_QUERY).matches;
}

export function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState<boolean>(readMatch);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }
    const mql = window.matchMedia(DESKTOP_QUERY);
    const onChange = (event: MediaQueryListEvent): void => {
      setIsDesktop(event.matches);
    };
    // Sync once in case the value changed between initial read and effect.
    setIsDesktop(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  return isDesktop;
}
